/*
 * nadi-cookie-prefs.js — make the "ניהול העדפות עוגיות" button actually work.
 *
 * WHAT IS BROKEN. The cookie policy page carries a button labelled
 * "ניהול העדפות עוגיות". Measured 2026-08-11, its href is
 *
 *     https://web-sandbox.oaiusercontent.com/?#reopenBanner
 *
 * — a ChatGPT sandbox domain that has nothing to do with this store. The page
 * was drafted in ChatGPT and the relative links were resolved against the
 * sandbox's own origin before being pasted in. So the one control on the page
 * that lets a visitor change their consent sends them off-site instead.
 *
 * That is not only untidy. The page promises, in writing, that consent can be
 * changed "בכל עת באמצעות הקישור". A dead link makes that sentence false.
 *
 * ── WHY A SCRIPT AND NOT JUST A CORRECTED HREF ────────────────────────────
 * There is no URL that reopens the banner. Pandectes exposes it as a function
 * call. Measured on the live page, window.Pandectes.fn contains:
 *
 *     showBanner, hideBanner, openPreferences, closePreferences,
 *     savePreferences, revokeConsent, applyConsent, ...
 *
 * openPreferences() is the one that opens the preferences panel — the panel
 * the page's own text describes as "התאמת העדפות".
 *
 * ── WHY IT MATCHES ON MORE THAN ONE THING ─────────────────────────────────
 * The handler fires on the button's class, on the sandbox href, and on a bare
 * "#reopenBanner". That is deliberate: the class is what the page has today,
 * the href is what a copy of this page elsewhere on the site would have, and
 * "#reopenBanner" is the anchor whoever wrote the page clearly intended. All
 * three are the same intent, so all three should do the same thing — and the
 * button keeps working whether or not the page body is corrected later.
 *
 * If Pandectes has not loaded, the click is left alone rather than swallowed.
 * A link that does nothing is worse than a link that goes somewhere wrong.
 */
(function () {
  'use strict';

  var SELECTOR = [
    '.cookie-settings-button',
    'a[href*="reopenBanner"]',
    'a[href$="#reopenBanner"]'
  ].join(',');

  function openPreferences() {
    var P = window.Pandectes;
    if (!P || !P.fn || typeof P.fn.openPreferences !== 'function') return false;
    try {
      P.fn.openPreferences();
      return true;
    } catch (e) {
      return false;
    }
  }

  /* One delegated listener on the document, so it covers links that are added
     later — the footer, a drawer, a second policy page — without re-binding. */
  document.addEventListener('click', function (ev) {
    var link = ev.target && ev.target.closest ? ev.target.closest(SELECTOR) : null;
    if (!link) return;
    if (openPreferences()) {
      ev.preventDefault();
      ev.stopPropagation();
    }
    /* else: fall through and let the browser do whatever the href says */
  }, true);
})();

/* ═══════════════════════════════════════════════════════════════════════
 * Reserve room under the consent banner.               added 2026-08-27
 *
 * THE BUG THIS FIXES. Nadav: "אי אפשר למחוק מוצר מהעגלה". Measured on
 * production at 375x812, cart page, two line items:
 *
 *   trash button @455  ->  tap lands on DIV.pd-cookie-banner-window
 *                          position:fixed, 375x205 @412, z-index 2147483647
 *   trash button @744  ->  tap lands on the fixed toolbar, z-index 190
 *
 * Hiding the banner made the same button work on the first tap — 2 items
 * became 1. So the control was never broken; it was buried under 205px of
 * consent banner carrying the maximum possible z-index, plus 55px of toolbar.
 *
 * WHY PADDING IS THE ACTUAL FIX AND NOT A DODGE. A bottom-fixed overlay always
 * covers the bottom of the viewport — that cannot be styled away, and a
 * customer can normally scroll the row they want up out of the covered strip.
 * The trap is at the END of the document: once scrolled to the bottom there is
 * nothing left to scroll, so the last 260px of the cart is unreachable for as
 * long as the banner is up. Reserving that height gives the page somewhere to
 * scroll to, which makes every row reachable rather than merely most of them.
 *
 * It is measured, not hard-coded: the banner's height is whatever Pandectes
 * renders at that viewport, and it changes with text length and language.
 */
(function () {
  'use strict';

  var BANNER = '.pd-cookie-banner-window, #pandectes-banner .cc-window';
  var PROP = '--nadi-consent-reserve';

  /* IS THIS BANNER ACTUALLY ON SCREEN?                    rewritten 2026-09-06
   *
   * Pandectes does not remove the banner on accept and does not set display or
   * visibility. It sets OPACITY: 0 — on the window and on its #pandectes-banner
   * wrapper — and leaves the element laid out at its full 205px.
   *
   * The test this replaces read display, visibility and height, so all three
   * said "visible" forever after the customer accepted. Measured on the draft:
   * opacity 0, display flex, visibility visible, height 205.
   *
   * That was not cosmetic. It left --nadi-consent-reserve at 206px, so every
   * page carried 206px of dead padding for the rest of the session, and it
   * held html.nadi-consent-up on, which hides the sticky add-to-cart bar —
   * meaning the buy button would have disappeared for good the moment anyone
   * accepted cookies. Found by checking after the change rather than before.
   *
   * checkVisibility({opacityProperty}) is the built-in answer and walks
   * ancestors itself. The fallback does the same walk by hand for browsers
   * without it. */
  function painted(el) {
    if (!el) return false;
    /* THE BANNER'S STATE CLASS, NOT ITS COMPUTED OPACITY.    corrected 2026-09-11
     *
     * Pandectes fades the banner: it toggles cc-invisible at the START of the
     * fade and opacity follows over the transition. Reading opacity at the
     * moment the class mutates therefore reads the state being LEFT. Measured on
     * the 11/9 draft: after showBanner the banner was fully visible with
     * nadi-consent-up OFF; after hideBanner it was gone with the class ON and
     * the buy bar hidden; only a later transitionend corrected either. The class
     * is the target state and changes synchronously, so it decides. Opacity is
     * no longer consulted at all. */
    if (el.classList && el.classList.contains('cc-invisible')) return false;
    if (typeof el.checkVisibility === 'function' &&
        !el.checkVisibility({ visibilityProperty: true })) return false;
    var n = el;
    while (n && n.nodeType === 1) {
      var cs = getComputedStyle(n);
      if (cs.display === 'none' || cs.visibility === 'hidden') return false;
      n = n.parentElement;
    }
    return el.getBoundingClientRect().height > 0;
  }

  function reserve() {
    var el = document.querySelector(BANNER);
    /* Only a banner that is actually painted counts. A dismissed one stays in
       the DOM at FULL height with opacity 0, and reserving space for it left a
       permanent 206px gap at the foot of every page. */
    var h = painted(el) ? Math.ceil(el.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty(PROP, h ? h + 'px' : '0px');
  }

  function start() {
    reserve();
    /* ATTRIBUTES, NOT JUST CHILD NODES.                    corrected 2026-09-06
       Pandectes shows and hides this banner by changing OPACITY on the element
       and its wrapper. That is an attribute mutation, and a childList observer
       never sees it — caught by toggling the banner both ways after the fix and
       finding it tracked one direction and not the other. Filtered to style and
       class so this stays cheap. */
    new MutationObserver(reserve).observe(document.body, {
      childList: true, subtree: true,
      attributes: true, attributeFilter: ['style', 'class']
    });
    window.addEventListener('resize', reserve);
    /* Opacity is faded, not switched. A transition end fires no mutation, so
       without this the state can lag a fade — harmless in the safe direction
       (the bar simply stays visible) but there is no reason to leave it. */
    document.addEventListener('transitionend', reserve, true);
    document.addEventListener('click', function () { setTimeout(reserve, 350); }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/* ═══════════════════════════════════════════════════════════════════════
 * Tell CSS when the banner is actually painted.            added 2026-09-06
 *
 * The block above already answers the only hard question here: is there a
 * consent banner ON SCREEN right now, as opposed to one sitting in the DOM at
 * zero height after it was dismissed? CSS cannot ask that — :has() would match
 * the dismissed leftover and would then hide the sticky buy bar forever, which
 * is a far worse bug than the one being fixed.
 *
 * So the measurement publishes a class as well as a length. Rules keyed on
 * html.nadi-consent-up apply only while a banner is genuinely visible.
 */
(function () {
  'use strict';

  var BANNER = '.pd-cookie-banner-window, #pandectes-banner .cc-window';
  var CLASS = 'nadi-consent-up';

  /* IS THIS BANNER ACTUALLY ON SCREEN?                    rewritten 2026-09-06
   *
   * Pandectes does not remove the banner on accept and does not set display or
   * visibility. It sets OPACITY: 0 — on the window and on its #pandectes-banner
   * wrapper — and leaves the element laid out at its full 205px.
   *
   * The test this replaces read display, visibility and height, so all three
   * said "visible" forever after the customer accepted. Measured on the draft:
   * opacity 0, display flex, visibility visible, height 205.
   *
   * That was not cosmetic. It left --nadi-consent-reserve at 206px, so every
   * page carried 206px of dead padding for the rest of the session, and it
   * held html.nadi-consent-up on, which hides the sticky add-to-cart bar —
   * meaning the buy button would have disappeared for good the moment anyone
   * accepted cookies. Found by checking after the change rather than before.
   *
   * checkVisibility({opacityProperty}) is the built-in answer and walks
   * ancestors itself. The fallback does the same walk by hand for browsers
   * without it. */
  function painted(el) {
    if (!el) return false;
    /* THE BANNER'S STATE CLASS, NOT ITS COMPUTED OPACITY.    corrected 2026-09-11
     *
     * Pandectes fades the banner: it toggles cc-invisible at the START of the
     * fade and opacity follows over the transition. Reading opacity at the
     * moment the class mutates therefore reads the state being LEFT. Measured on
     * the 11/9 draft: after showBanner the banner was fully visible with
     * nadi-consent-up OFF; after hideBanner it was gone with the class ON and
     * the buy bar hidden; only a later transitionend corrected either. The class
     * is the target state and changes synchronously, so it decides. Opacity is
     * no longer consulted at all. */
    if (el.classList && el.classList.contains('cc-invisible')) return false;
    if (typeof el.checkVisibility === 'function' &&
        !el.checkVisibility({ visibilityProperty: true })) return false;
    var n = el;
    while (n && n.nodeType === 1) {
      var cs = getComputedStyle(n);
      if (cs.display === 'none' || cs.visibility === 'hidden') return false;
      n = n.parentElement;
    }
    return el.getBoundingClientRect().height > 0;
  }

  function sync() {
    document.documentElement.classList.toggle(CLASS, painted(document.querySelector(BANNER)));
  }

  function start() {
    sync();
    /* ATTRIBUTES, NOT JUST CHILD NODES.                    corrected 2026-09-06
       Pandectes shows and hides this banner by changing OPACITY on the element
       and its wrapper. That is an attribute mutation, and a childList observer
       never sees it — caught by toggling the banner both ways after the fix and
       finding it tracked one direction and not the other. Filtered to style and
       class so this stays cheap. */
    new MutationObserver(sync).observe(document.body, {
      childList: true, subtree: true,
      attributes: true, attributeFilter: ['style', 'class']
    });
    window.addEventListener('resize', sync);
    /* Opacity is faded, not switched. A transition end fires no mutation, so
       without this the state can lag a fade — harmless in the safe direction
       (the bar simply stays visible) but there is no reason to leave it. */
    document.addEventListener('transitionend', sync, true);
    document.addEventListener('click', function () { setTimeout(sync, 350); }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
